import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Table,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Input,
  InputGroup,
  InputGroupAddon,
  Button,
  Card,
  CardBody,
  ButtonGroup,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import Widget from '../../../components/Widget';
import config from '../../../config';
import s from './TripEscalations.module.scss';
import { useCity } from '../../../contexts/CityContext';
import { getCustomerDisplayName } from '../../../utils/customerUtils';

// Create axios instance for dispatch API (port 8001)
const escalationApi = axios.create({
  baseURL: config.dispatchApiUrl,
});

// Add auth interceptor
escalationApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

class TripEscalations extends Component {
  constructor(props) {
    super(props);
    
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];
    
    this.state = {
      searchTerm: '',
      escalations: [],
      stats: {
        total_escalations: 0,
        pickup_delays: 0,
        destination_delays: 0,
        completion_delays: 0,
        high_severity: 0,
        medium_severity: 0,
        enroute_alerts: 0,
      },
      isFetching: false,
      error: null,
      currentTime: null,
      filterType: 'all', // all, pickup_delay, destination_delay, completion_delay
      filterDate: today, // Default to today
      cancellationEscalations: [],
      cancellationStats: {
        total_escalations: 0,
        pending: 0,
        resolved: 0,
        en_route_to_pickup_cancellations: 0,
        at_pickup_cancellations: 0,
      },
      cancellationFilter: 'pending', // 'all', 'pending', 'resolved'
      isFetchingCancellations: false,
      cancellationError: null,
      resolvingId: null,
      categoryFilter: 'all', // all | trip | cancellation
      selectedEscalation: null,
      showDetailModal: false,
      // Idle Drivers state
      idleDrivers: [],
      showIdleModal: false,
      selectedIdleDriver: null,
      showIdleDetailModal: false,
      sendingReturnToBase: null, // driver_id being sent
      cancellingTrip: false,
      // Idle driver location name cache: { "lat,lng": "Location Name" }
      idleLocationNames: {},
      idleLocationLoading: {},
      // Dismissed idle driver keys: Set of "driverId:tripId" strings (persisted in localStorage)
      dismissedIdleKeys: (() => {
        try {
          const stored = localStorage.getItem('dismissedIdleKeys');
          return stored ? new Set(JSON.parse(stored)) : new Set();
        } catch { return new Set(); }
      })(),
      // Enroute Movement Alerts
      enrouteAlerts: [],
      selectedEnrouteAlert: null,
      showEnrouteModal: false,
      // Pickup Delay Alerts (Ops Agent)
      pickupDelayAlerts: [],
      selectedPickupAlert: null,
      showPickupDelayModal: false,
      // Drop Delay Alerts (Ops Agent)
      dropDelayAlerts: [],
      selectedDropAlert: null,
      showDropDelayModal: false,
      // Send Message state
      sendingMessage: false,
      sentMessages: new Set(), // Track trip IDs that have already been messaged
      // Dismissed trip IDs (to filter from ALL data sources including legacy API)
      dismissedTripIds: new Set(),
      // Dismiss reason prompt
      dismissPending: null,   // { type: 'alert'|'idle', args: [...] }
      dismissReason: '',
    };
  }

  componentDidMount() {
    this.fetchAllEscalations();
    this.fetchSentMessages();
    // Auto-refresh every 30 seconds
    this.refreshInterval = setInterval(() => {
      this.fetchAllEscalations();
      this.fetchSentMessages();
    }, 30000);
    // Connect WebSocket for idle drivers
    this.connectIdleDriversWebSocket();

    // Clear stale dismissed idle keys from previous day
    try {
      const today = new Date().toISOString().split('T')[0];
      const storedDate = localStorage.getItem('dismissedIdleKeys_date');
      if (storedDate !== today) {
        localStorage.removeItem('dismissedIdleKeys');
        localStorage.setItem('dismissedIdleKeys_date', today);
        this.setState({ dismissedIdleKeys: new Set() });
      }
    } catch {}

    // Check for navigation intent to show Idle Drivers filter
    if (this.props.location?.state?.openIdleDrivers) {
      this.setState({ filterType: 'idle_driver' });
      // Clear state to prevent re-trigger on refresh
      const state = { ...this.props.location.state };
      delete state.openIdleDrivers;
      this.props.history.replace({ ...this.props.location, state });
    }
  }

  componentDidUpdate(prevProps) {
    // Refetch when selected cities change
    if (JSON.stringify(prevProps.selectedCities) !== JSON.stringify(this.props.selectedCities)) {
      this.fetchAllEscalations();
    }
  }

  componentWillUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    // Close idle drivers WebSocket
    if (this.idleWs) {
      this.idleWs.close(1000, 'Component unmounting');
      this.idleWs = null;
    }
  }

  // ── Idle Drivers WebSocket ────────────────────────────────────────
  connectIdleDriversWebSocket = () => {
    try {
      const wsUrl = config.escalationWebsocket || 'ws://localhost:5005/ws/ops-escalation';
      console.log('🏠 Connecting idle drivers WebSocket:', wsUrl);
      this.idleWs = new WebSocket(wsUrl);

      this.idleWs.onopen = () => {
        console.log('✅ Idle drivers WebSocket connected');
      };

      this.idleWs.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'idle_drivers') {
            console.log('🏠 Received idle drivers:', data.count);
            // Filter out dismissed idle drivers before updating state
            const dismissed = this.state.dismissedIdleKeys;
            const filteredDrivers = (data.drivers || []).filter(d => {
              const key = `${d.driver_id}:${d.last_trip_id || ''}`;
              return !dismissed.has(key);
            });
            this.setState({ idleDrivers: filteredDrivers });
          }
        } catch (e) {
          // Ignore non-idle messages
        }
      };

      this.idleWs.onclose = (event) => {
        console.log('❌ Idle drivers WebSocket closed', event.code);
        // Reconnect after 5 seconds if not intentional close
        if (event.code !== 1000) {
          setTimeout(() => this.connectIdleDriversWebSocket(), 5000);
        }
      };

      this.idleWs.onerror = (error) => {
        console.error('Idle drivers WebSocket error:', error);
      };
    } catch (e) {
      console.error('Failed to connect idle drivers WebSocket:', e);
    }
  }

  handleReturnToBase = async (driverId) => {
    this.setState({ sendingReturnToBase: driverId });
    try {
      const response = await fetch(`${config.dispatchApiBase}/return-to-base`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driver_id: driverId }),
      });
      const result = await response.json();
      if (result.success) {
        toast.success(`✅ Return to base sent to Driver ${driverId}`);
        // Update driver state locally to show blue card IMMEDIATELY
        this.setState(prev => ({
          idleDrivers: prev.idleDrivers.map(d => {
            if (d.driver_id === driverId) {
              return {
                ...d,
                rtb_state: 'RETURNING',
                reason: 'Driver returning to base.',
              };
            }
            return d;
          }),
          // Close the modal
          showIdleDetailModal: false,
          selectedIdleDriver: null,
        }));
      } else {
        toast.error(`❌ Failed: ${result.detail || result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Return to base error:', error);
      toast.error(`❌ Error: ${error.message}`);
    } finally {
      this.setState({ sendingReturnToBase: null });
    }
  }

  fetchAllEscalations = () => {
    this.fetchEscalations();
    this.fetchCancellationEscalations();
    this.fetchEnrouteAlerts();
    this.fetchPickupDelayAlerts();
    this.fetchDropDelayAlerts();
  }

  fetchEnrouteAlerts = async () => {
    try {
      const response = await fetch(`${config.dispatchApiBase}/enroute-movement-alerts`);
      const data = await response.json();
      if (data.success && data.alerts) {
        const dismissed = this.state.dismissedTripIds;
        const filtered = data.alerts.filter(a => !dismissed.has(String(a.trip_id)));
        this.setState({ enrouteAlerts: filtered });
      }
    } catch (error) {
      console.error('Failed to fetch enroute alerts:', error);
    }
  }

  fetchPickupDelayAlerts = async () => {
    try {
      const response = await fetch(`${config.dispatchApiBase}/pickup-delay-alerts`);
      const data = await response.json();
      if (data.success && data.alerts) {
        const dismissed = this.state.dismissedTripIds;
        const filtered = data.alerts.filter(a => !dismissed.has(String(a.trip_id)));
        this.setState({ pickupDelayAlerts: filtered });
      }
    } catch (error) {
      console.error('Failed to fetch pickup delay alerts:', error);
    }
  }

  fetchDropDelayAlerts = async () => {
    try {
      const response = await fetch(`${config.dispatchApiBase}/drop-delay-alerts`);
      const data = await response.json();
      if (data.success && data.alerts) {
        const dismissed = this.state.dismissedTripIds;
        const filtered = data.alerts.filter(a => !dismissed.has(String(a.trip_id)));
        this.setState({ dropDelayAlerts: filtered });
      }
    } catch (error) {
      console.error('Failed to fetch drop delay alerts:', error);
    }
  }

  fetchSentMessages = async () => {
    try {
      const response = await fetch(`${config.dispatchApiBase}/escalation-messages-sent`);
      const data = await response.json();
      if (data.success && data.sent_trip_ids) {
        this.setState(prev => ({
          sentMessages: new Set([...prev.sentMessages, ...data.sent_trip_ids.map(String)]),
        }));
      }
    } catch (error) {
      console.error('Failed to fetch sent messages:', error);
    }
  }

  handleSendMessage = async (tripId, message) => {
    if (!tripId || !message) {
      toast.warning('Trip ID or message not available');
      return;
    }
    this.setState({ sendingMessage: true });
    try {
      const response = await axios.post(
        `${config.opsApiBase}/trips/${tripId}/escalation-alert`,
        { message },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) {
        toast.success(`✅ Message sent to ${response.data.driver_name || 'driver'} via WhatsApp`);
        this.setState(prev => ({
          sentMessages: new Set(prev.sentMessages).add(String(tripId)),
        }));
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Send message error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send WhatsApp message');
    } finally {
      this.setState({ sendingMessage: false });
    }
  }

  formatMinutesForMessage = (value) => {
    if (value === null || value === undefined) return '?';
    const num = Number(value);
    if (!Number.isFinite(num)) return '?';
    return String(Math.round(num));
  }

  handleSendMessageWithConfirm = (tripId, message) => {
    if (this.state.sentMessages.has(String(tripId))) {
      if (!window.confirm('This message has already been sent once. Do you want to send it again?')) {
        return;
      }
    }
    this.handleSendMessage(tripId, message);
  }

  fetchEscalations = async () => {
    this.setState({ isFetching: true, error: null });
    try {
      const { filterType, filterDate } = this.state;
      const { selectedCities } = this.props;
      
      // Build cities parameter
      const citiesParam = selectedCities && selectedCities.length > 0 
        ? `&cities=${selectedCities.join(',')}` 
        : '';
      
      // Try primary endpoint, fallback to localhost if it fails
      let escalationsRes, statsRes;
      try {
        [escalationsRes, statsRes] = await Promise.all([
          escalationApi.get(`/trip-escalations?escalation_type=${filterType}&date=${filterDate}${citiesParam}`),
          escalationApi.get(`/trip-escalations/stats?date=${filterDate}${citiesParam}`),
        ]);
      } catch (primaryError) {
        console.warn('Primary escalations endpoint failed, using fallback:', primaryError);
        [escalationsRes, statsRes] = await Promise.all([
          axios.get(`${config.dispatchApiUrl}/trip-escalations?escalation_type=${filterType}&date=${filterDate}${citiesParam}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }),
          axios.get(`${config.dispatchApiUrl}/trip-escalations/stats?date=${filterDate}${citiesParam}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }),
        ]);
      }
      
      this.setState({ 
        escalations: escalationsRes.data.escalations || [],
        stats: statsRes.data.stats || {},
        currentTime: escalationsRes.data.current_time,
        isFetching: false 
      });
    } catch (error) {
      console.error('Failed to fetch escalations:', error);
      this.setState({ 
        error: error.response?.data?.detail || 'Failed to fetch escalations',
        isFetching: false 
      });
      toast.error('Failed to fetch trip escalations');
    }
  }

  // Cancellation Escalations Functions
  fetchCancellationEscalations = async () => {
    this.setState({ isFetchingCancellations: true, cancellationError: null });
    try {
      const { cancellationFilter, filterDate } = this.state;
      const { selectedCities } = this.props;
      
      // Build cities parameter
      const citiesParam = selectedCities && selectedCities.length > 0 
        ? `&cities=${selectedCities.join(',')}` 
        : '';
      
      // Try primary endpoint, fallback to localhost if it fails
      let escalationsRes, statsRes;
      try {
        [escalationsRes, statsRes] = await Promise.all([
          escalationApi.get(`/cancellation-escalations?status=${cancellationFilter}&date=${filterDate}${citiesParam}`),
          escalationApi.get(`/cancellation-escalations/stats?date=${filterDate}${citiesParam}`),
        ]);
      } catch (primaryError) {
        console.warn('Primary cancellation escalations endpoint failed, using fallback:', primaryError);
        [escalationsRes, statsRes] = await Promise.all([
          axios.get(`${config.dispatchApiUrl}/cancellation-escalations?status=${cancellationFilter}&date=${filterDate}${citiesParam}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }),
          axios.get(`${config.dispatchApiUrl}/cancellation-escalations/stats?date=${filterDate}${citiesParam}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }),
        ]);
      }
      
      this.setState({ 
        cancellationEscalations: escalationsRes.data.escalations || [],
        cancellationStats: statsRes.data.stats || {},
        currentTime: escalationsRes.data.current_time,
        isFetchingCancellations: false 
      });
    } catch (error) {
      console.error('Failed to fetch cancellation escalations:', error);
      this.setState({ 
        cancellationError: error.response?.data?.detail || 'Failed to fetch cancellation escalations',
        isFetchingCancellations: false 
      });
      toast.error('Failed to fetch cancellation escalations');
    }
  }

  handleResolveCancellation = async (escalationId) => {
    const resolvedBy = prompt('Enter your name to resolve this escalation:');
    if (!resolvedBy) return;
    
    const resolutionNotes = prompt('Enter resolution notes (optional):') || '';
    
    this.setState({ resolvingId: escalationId });
    try {
      await escalationApi.put(`/cancellation-escalations/${escalationId}/resolve`, {
        resolved_by: resolvedBy,
        resolution_notes: resolutionNotes,
      });
      
      toast.success('Escalation resolved successfully');
      this.fetchCancellationEscalations();
    } catch (error) {
      console.error('Failed to resolve escalation:', error);
      toast.error(error.response?.data?.detail || 'Failed to resolve escalation');
    } finally {
      this.setState({ resolvingId: null });
    }
  }

  handleCancellationFilterChange = (filter) => {
    this.setState({ cancellationFilter: filter }, () => {
      this.fetchCancellationEscalations();
    });
  }

  filterCancellationEscalations = (escalations) => {
    const searchLower = this.state.searchTerm.toLowerCase();
    if (!searchLower) return escalations;
    
    return escalations.filter(escalation => {
      const matchesSearch = 
        String(escalation.booking_id || '').toLowerCase().includes(searchLower) ||
        String(escalation.vehicle_id || '').includes(searchLower) ||
        (escalation.driver_name || '').toLowerCase().includes(searchLower) ||
        (escalation.customer_name || '').toLowerCase().includes(searchLower) ||
        (escalation.customer_business_name || '').toLowerCase().includes(searchLower) ||
        (escalation.pickup_location || '').toLowerCase().includes(searchLower);
      return matchesSearch;
    });
  }

  handleSearch = (e) => {
    this.setState({ searchTerm: e.target.value });
  }

  handleFilterChange = (filterType) => {
    this.setState({ filterType }, () => {
      this.fetchEscalations();
    });
  }

  handleDateChange = (e) => {
    this.setState({ filterDate: e.target.value }, () => {
      this.fetchEscalations();
    });
  }

  goToToday = () => {
    const today = new Date().toISOString().split('T')[0];
    this.setState({ filterDate: today }, () => {
      this.fetchEscalations();
    });
  }

  goToPreviousDay = () => {
    const currentDate = new Date(this.state.filterDate);
    currentDate.setDate(currentDate.getDate() - 1);
    this.setState({ filterDate: currentDate.toISOString().split('T')[0] }, () => {
      this.fetchEscalations();
    });
  }

  goToNextDay = () => {
    const currentDate = new Date(this.state.filterDate);
    currentDate.setDate(currentDate.getDate() + 1);
    this.setState({ filterDate: currentDate.toISOString().split('T')[0] }, () => {
      this.fetchEscalations();
    });
  }

  isToday = () => {
    const today = new Date().toISOString().split('T')[0];
    return this.state.filterDate === today;
  }

  filterEscalations = (escalations) => {
    const searchLower = this.state.searchTerm.toLowerCase();
    if (!searchLower) return escalations;
    
    return escalations.filter(escalation => {
      const matchesSearch = 
        String(escalation.booking_id || '').toLowerCase().includes(searchLower) ||
        String(escalation.trip_id || '').includes(searchLower) ||
        String(escalation.vehicle_id || '').includes(searchLower) ||
        (escalation.driver_name || '').toLowerCase().includes(searchLower) ||
        (escalation.vehicle_registration || '').toLowerCase().includes(searchLower) ||
        (escalation.pickup_location || '').toLowerCase().includes(searchLower) ||
        (escalation.drop_location || '').toLowerCase().includes(searchLower);
      return matchesSearch;
    });
  }

  formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  }

  formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-IN', { 
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  }

  getTypeBadge = (type) => {
    if (type === 'pickup_delay') {
      return <Badge color="warning" className={s.typeBadge}>🚚 Pickup Delay</Badge>;
    } else if (type === 'destination_delay') {
      return <Badge color="danger" className={s.typeBadge}>📍 Destination Delay</Badge>;
    } else if (type === 'completion_delay') {
      return <Badge color="info" className={s.typeBadge}>🏁 Completion Delay</Badge>;
    }
    return <Badge color="secondary">{type}</Badge>;
  }

  getSeverityBadge = (severity) => {
    if (severity === 'high') {
      return <Badge color="danger" className={s.severityBadge}>🔴 HIGH</Badge>;
    }
    return <Badge color="warning" className={s.severityBadge}>🟡 MEDIUM</Badge>;
  }

  getStatusBadge = (status) => {
    // Status enum values from database: assigned, night_confirmed, morning_pending, 
    // morning_confirmed, route_sent, en_route_pickup, at_pickup, loaded, en_route_drop, 
    // at_drop, unloaded, completed, cancelled, escalated, immediate_pending, 
    // pending_acceptance, cancelled_by_customer, delayed, received, timeout, accepted
    const statusMap = {
      'assigned': { color: 'secondary', text: 'Assigned' },
      'accepted': { color: 'primary', text: 'Accepted' },
      'pending_acceptance': { color: 'warning', text: 'Pending Acceptance' },
      'night_confirmed': { color: 'info', text: 'Night Confirmed' },
      'morning_confirmed': { color: 'info', text: 'Morning Confirmed' },
      'morning_pending': { color: 'warning', text: 'Morning Pending' },
      'route_sent': { color: 'info', text: 'Route Sent' },
      'en_route_pickup': { color: 'info', text: 'En Route to Pickup' },
      'at_pickup': { color: 'success', text: 'At Pickup' },
      'loaded': { color: 'success', text: 'Loaded' },
      'en_route_drop': { color: 'info', text: 'En Route to Drop' },
      'at_drop': { color: 'success', text: 'At Drop' },
      'unloaded': { color: 'success', text: 'Unloaded' },
      'completed': { color: 'success', text: 'Completed' },
      'cancelled': { color: 'danger', text: 'Cancelled' },
      'cancelled_by_customer': { color: 'danger', text: 'Cancelled by Customer' },
      'escalated': { color: 'danger', text: 'Escalated' },
      'delayed': { color: 'warning', text: 'Delayed' },
      'timeout': { color: 'danger', text: 'Timeout' },
    };
    const statusInfo = statusMap[status] || { color: 'secondary', text: status || 'Unknown' };
    return <Badge color={statusInfo.color}>{statusInfo.text}</Badge>;
  }

  formatOverdue = (minutes) => {
    if (!minutes || minutes <= 0) return '-';
    const totalMins = Math.floor(minutes);
    if (totalMins < 60) {
      return `${totalMins} min`;
    }
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  handleCallDriver = (phone) => {
    if (phone && phone !== 'N/A') {
      window.location.href = `tel:${phone}`;
    } else {
      toast.warning('Driver phone number not available');
    }
  }

  handleCancelTrip = async (alert) => {
    if (!alert || !alert.booking_id) {
      toast.error('Booking ID not found');
      return;
    }
    if (!window.confirm(`Cancel trip for booking ${alert.booking_id}? This will cancel the assignment, trip, and booking.`)) {
      return;
    }
    this.setState({ cancellingTrip: true });
    try {
      await axios.put(
        `${config.dispatchApiBase}/assignments/${alert.booking_id}/cancel`,
        {
          reason: 'Cancelled by Ops team via Enroute Alert',
          modified_by: 'ops_agent',
        }
      );
      toast.success(`Trip for booking ${alert.booking_id} cancelled successfully`);
      this.setState({ showEnrouteModal: false, selectedEnrouteAlert: null });
      this.fetchAllEscalations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel trip');
    } finally {
      this.setState({ cancellingTrip: false });
    }
  }

  /**
   * Dismiss an alert from the dashboard WITHOUT cancelling the trip.
   * The alert will not reappear in future ops agent cycles for this alert type,
   * but the trip continues normally. Other alert types (e.g. drop delay) can
   * still trigger for the same trip.
   *
   * @param {object} alert - The alert object (must have trip_id)
   * @param {string} alertType - One of: 'pickup_delay', 'drop_delay', 'enroute'
   * @param {string} modalStateKey - State key to close the modal (e.g. 'showPickupDelayModal')
   * @param {string} selectedStateKey - State key to clear the selected alert (e.g. 'selectedPickupAlert')
   * @param {string} listStateKey - State key for the alerts array (e.g. 'pickupDelayAlerts')
   */
  handleDismissClick = (type, ...args) => {
    this.setState({ dismissPending: { type, args }, dismissReason: '' });
  }

  handleDismissConfirm = () => {
    const { dismissPending, dismissReason } = this.state;
    if (!dismissPending || !dismissReason.trim()) return;
    this.setState({ dismissPending: null, dismissReason: '' });
    if (dismissPending.type === 'alert') {
      this.handleDismissAlert(...dismissPending.args, dismissReason.trim());
    } else {
      this.handleDismissIdleDriver(dismissPending.args[0], dismissReason.trim());
    }
  }

  handleDismissAlert = async (alert, alertType, modalStateKey, selectedStateKey, listStateKey, reason = '') => {
    if (!alert || !alert.trip_id) {
      toast.error('Trip ID not found');
      return;
    }
    this.setState({ cancellingTrip: true });
    try {
      const response = await fetch(`${config.opsApiBase}/dismiss-alert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trip_id: alert.trip_id, alert_type: alertType, reason }),
      });
      const result = await response.json();
      if (result.success) {
        toast.success(`Alert dismissed for trip #${alert.trip_id}. It will not reappear.`);
        // Remove from local state immediately AND track dismissed trip ID
        this.setState(prev => ({
          [listStateKey]: (prev[listStateKey] || []).filter(a => a.trip_id !== alert.trip_id),
          [modalStateKey]: false,
          [selectedStateKey]: null,
          dismissedTripIds: new Set(prev.dismissedTripIds).add(String(alert.trip_id)),
        }));
      } else {
        toast.error(`Failed to dismiss: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Dismiss alert error:', err);
      toast.error(`Error dismissing alert: ${err.message}`);
    } finally {
      this.setState({ cancellingTrip: false });
    }
  }

  /**
   * Reverse geocode a lat/lng to a human-readable location name.
   * Results are cached in state to avoid repeated API calls.
   */
  reverseGeocodeLocation = (lat, lng) => {
    if (!lat || !lng) return;
    const cacheKey = `${lat},${lng}`;
    if (this.state.idleLocationNames[cacheKey] || this.state.idleLocationLoading[cacheKey]) return;

    this.setState(prev => ({ idleLocationLoading: { ...prev.idleLocationLoading, [cacheKey]: true } }));

    fetch(`${config.opsApiBase}/reverse-geocode?lat=${lat}&lng=${lng}`)
      .then(res => res.json())
      .then(data => {
        const name = data.location_name || `${lat}, ${lng}`;
        this.setState(prev => ({
          idleLocationNames: { ...prev.idleLocationNames, [cacheKey]: name },
          idleLocationLoading: { ...prev.idleLocationLoading, [cacheKey]: false },
        }));
      })
      .catch(err => {
        console.error('Reverse geocode error:', err);
        this.setState(prev => ({
          idleLocationNames: { ...prev.idleLocationNames, [cacheKey]: `${lat}, ${lng}` },
          idleLocationLoading: { ...prev.idleLocationLoading, [cacheKey]: false },
        }));
      });
  }

  /**
   * Dismiss an idle driver card from the dashboard (per-state).
   * Only the current card state (e.g. IDLE/yellow) is dismissed.
   * Other states (RETURNING/blue, etc.) can still appear.
   */
  handleDismissIdleDriver = async (driver, reason = '') => {
    if (!driver || !driver.driver_id) {
      toast.error('Driver ID not found');
      return;
    }
    const rtbState = driver._rtbState || driver.rtb_state || 'IDLE';
    this.setState({ cancellingTrip: true });
    try {
      const response = await fetch(`${config.opsApiBase}/dismiss-alert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alert_type: 'idle_driver',
          driver_id: driver.driver_id,
          rtb_state: rtbState,
          last_trip_id: driver.last_trip_id || '',
          reason,
        }),
      });
      const result = await response.json();
      if (result.success) {
        toast.success(`Idle driver card dismissed for Vehicle #${driver.vehicle_id}.`);
        // Track dismissed key WITH trip_id so WebSocket pushes won't bring it back
        // but a NEW idle session (after a new trip) WILL show the card again
        const dismissKey = `${driver.driver_id}:${driver.last_trip_id || ''}`;
        // Remove from local state immediately AND persist to localStorage
        this.setState(prev => {
          const updatedKeys = new Set(prev.dismissedIdleKeys).add(dismissKey);
          try { localStorage.setItem('dismissedIdleKeys', JSON.stringify([...updatedKeys])); } catch {}
          return {
            idleDrivers: (prev.idleDrivers || []).filter(d =>
              !(d.driver_id === driver.driver_id && (d.last_trip_id || '') === (driver.last_trip_id || ''))
            ),
            showIdleDetailModal: false,
            selectedIdleDriver: null,
            dismissedIdleKeys: updatedKeys,
          };
        });
      } else {
        toast.error(`Failed to dismiss: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Dismiss idle driver error:', err);
      toast.error(`Error dismissing: ${err.message}`);
    } finally {
      this.setState({ cancellingTrip: false });
    }
  }

  render() {
    const { 
      searchTerm, escalations, stats, isFetching, error, filterType, currentTime, filterDate,
      cancellationEscalations, cancellationStats, cancellationFilter, 
      isFetchingCancellations, cancellationError, resolvingId,
      categoryFilter, selectedEscalation, showDetailModal,
      enrouteAlerts, selectedEnrouteAlert, showEnrouteModal,
      pickupDelayAlerts, selectedPickupAlert, showPickupDelayModal,
      dropDelayAlerts, selectedDropAlert, showDropDelayModal,
      sendingMessage
    } = this.state;
    const filteredEscalations = this.filterEscalations(escalations);
    const filteredCancellations = this.filterCancellationEscalations(cancellationEscalations);

    // Convert enroute alerts to escalation-like format so they merge into the table
    const enrouteAsEscalations = enrouteAlerts
      .filter(a => filterType === 'all' || filterType === 'enroute_alert')
      .map(a => ({
        ...a,
        _kind: 'trip',
        _isEnrouteAlert: true,
        escalation_type: 'pickup_delay',
        severity: a.severity || 'medium',
        trip_id: a.trip_id,
        booking_id: a.booking_id,
        driver_name: a.driver_name,
        driver_phone: a.driver_phone,
        vehicle_id: a.vehicle_id,
        vehicle_registration: a.vehicle_registration,
        pickup_location: a.pickup_location,
        drop_location: a.drop_location,
        scheduled_pickup_time: a.pickup_time,
        time_overdue_minutes: a.elapsed_minutes,
        escalation_reason: a.reason,
        created_at: a.flagged_at,
      }));

    // Convert pickup delay alerts to escalation-like format
    const pickupDelayAsEscalations = pickupDelayAlerts
      .filter(a => filterType === 'all' || filterType === 'pickup_delay')
      .map(a => ({
        ...a,
        _kind: 'trip',
        _isPickupDelayAlert: true,
        escalation_type: 'pickup_delay',
        severity: a.severity || 'medium',
        trip_id: a.trip_id,
        booking_id: a.booking_id,
        driver_name: a.driver?.name,
        driver_phone: a.driver?.phone,
        vehicle_id: a.driver?.vehicle_id,
        vehicle_registration: a.driver?.vehicle_registration,
        pickup_location: a.pickup_location,
        drop_location: a.drop_location,
        scheduled_pickup_time: a.pickup_time,
        time_overdue_minutes: a.time_overdue_minutes,
        escalation_reason: a.driver_message,
        created_at: a.flagged_at,
      }));

    // Convert drop delay alerts to escalation-like format
    const dropDelayAsEscalations = dropDelayAlerts
      .filter(a => filterType === 'all' || filterType === 'destination_delay' || filterType === 'drop_delay_alert')
      .map(a => ({
        ...a,
        _kind: 'trip',
        _isDropDelayAlert: true,
        escalation_type: 'drop_delay',
        severity: a.severity || 'medium',
        trip_id: a.trip_id,
        booking_id: a.booking_id,
        driver_name: a.driver_name,
        driver_phone: a.driver_phone,
        vehicle_id: a.vehicle_id,
        vehicle_registration: a.vehicle_registration,
        pickup_location: a.pickup_location,
        drop_location: a.drop_location,
        scheduled_pickup_time: a.pickup_time,
        time_overdue_minutes: a.elapsed_minutes,
        escalation_reason: a.reason,
        created_at: a.flagged_at,
      }));

    // Convert idle drivers to escalation-like rows (inline in table)
    const idleDriverAsEscalations = (this.state.idleDrivers || [])
      .filter(d => filterType === 'all' || filterType === 'idle_driver')
      .map(d => {
        const rtbState = d.rtb_state || 'IDLE';
        const isAtBase = rtbState === 'AT_BASE' || d.is_at_base || (d.reason && d.reason.includes('idle at base'));
        const isReturning = rtbState === 'RETURNING' || (d.reason && d.reason.includes('returning to base'));
        const isNotReachedBase = rtbState === 'NOT_REACHED_BASE' || (d.reason && d.reason.includes('not reached base yet'));
        let severityLevel = 'medium';
        if (isNotReachedBase) severityLevel = 'high';
        else if (isAtBase) severityLevel = 'low';
        return {
          ...d,
          _kind: 'trip',
          _isIdleDriverAlert: true,
          escalation_type: 'idle_driver',
          severity: severityLevel,
          trip_id: d.last_trip_id,
          booking_id: null,
          driver_name: d.driver_name || `Vehicle #${d.vehicle_id}`,
          driver_phone: d.phone_number,
          vehicle_id: d.vehicle_id,
          pickup_location: typeof d.location === 'string' ? d.location : (d.location ? `${d.location.lat || ''}, ${d.location.lng || ''}` : 'Unknown'),
          escalation_reason: d.reason,
          created_at: d.checked_at,
          _rtbState: rtbState,
          _isAtBase: isAtBase,
          _isReturning: isReturning,
          _isNotReachedBase: isNotReachedBase,
        };
      });

    // Collect booking_id + trip_id covered by agent monitors (dedupe vs legacy trip-escalations API)
    const agentBookingIds = new Set();
    const agentTripIds = new Set();
    [pickupDelayAsEscalations, dropDelayAsEscalations, enrouteAsEscalations].forEach((arr) => {
      arr.forEach((a) => {
        if (a.booking_id != null && a.booking_id !== '') agentBookingIds.add(String(a.booking_id));
        if (a.trip_id != null && a.trip_id !== '') agentTripIds.add(String(a.trip_id));
      });
    });

    const isDuplicateOfAgent = (e) => {
      if (e.booking_id != null && e.booking_id !== '' && agentBookingIds.has(String(e.booking_id))) return true;
      if (e.trip_id != null && e.trip_id !== '' && agentTripIds.has(String(e.trip_id))) return true;
      return false;
    };

    // Also filter out trips that were dismissed by the operator
    const { dismissedTripIds } = this.state;
    const isDismissed = (e) => {
      if (e.trip_id != null && e.trip_id !== '' && dismissedTripIds.has(String(e.trip_id))) return true;
      return false;
    };

    let combinedEscalations = [
      ...(filterType === 'enroute_alert' || filterType === 'idle_driver' ? [] : filteredEscalations
        .filter(e => !isDuplicateOfAgent(e) && !isDismissed(e))
        .map(e => ({ ...e, _kind: 'trip' }))),
      ...(filterType === 'enroute_alert' || filterType === 'idle_driver' ? [] : filteredCancellations.map(e => ({ ...e, _kind: 'cancellation' }))),
      ...enrouteAsEscalations,
      ...pickupDelayAsEscalations,
      ...dropDelayAsEscalations,
      ...idleDriverAsEscalations,
    ];

    if (categoryFilter === 'trip') {
      combinedEscalations = combinedEscalations.filter(e => e._kind === 'trip');
    } else if (categoryFilter === 'cancellation') {
      combinedEscalations = combinedEscalations.filter(e => e._kind === 'cancellation');
    }

    combinedEscalations.sort((a, b) => {
      const aTime = new Date(a.created_at || a.trip_started_at || a.scheduled_pickup_time || 0).getTime();
      const bTime = new Date(b.created_at || b.trip_started_at || b.scheduled_pickup_time || 0).getTime();
      return bTime - aTime;
    });
    
    // Format display date
    const displayDate = new Date(filterDate).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

    return (
      <div className={s.escalationsContainer}>
        <Breadcrumb>
          <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
          <BreadcrumbItem><Link to="/app/trips">Trips</Link></BreadcrumbItem>
          <BreadcrumbItem active>Escalations</BreadcrumbItem>
        </Breadcrumb>
        
        {/* Date Filter */}
        <Row className="mb-3">
          <Col sm={12}>
            <div className={s.dateFilterContainer}>
              <div className="d-flex align-items-center">
                <Button 
                  color="secondary" 
                  size="sm" 
                  onClick={this.goToPreviousDay}
                  className="mr-2"
                >
                  <i className="fa fa-chevron-left" />
                </Button>
                <Input
                  type="date"
                  value={filterDate}
                  onChange={this.handleDateChange}
                  className={s.dateInput}
                  style={{ width: '160px' }}
                />
                <Button 
                  color="secondary" 
                  size="sm" 
                  onClick={this.goToNextDay}
                  className="ml-2 mr-3"
                >
                  <i className="fa fa-chevron-right" />
                </Button>
                <span className={s.displayDate}>{displayDate}</span>
                {!this.isToday() && (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={this.goToToday}
                    className="ml-3"
                  >
                    Go to Today
                  </Button>
                )}
                {this.isToday() && (
                  <Badge color="success" className="ml-3">Today</Badge>
                )}

              </div>
            </div>
          </Col>
        </Row>
        
        {/* Combined Trip + Cancellation Escalations */}
        <Row className="mb-4">
          <Col sm={2}>
            <Card className={`${s.statCard} ${s.statTotal} ${s.compactCard}`} id="stat-total">
              <CardBody className="text-center">
                <h2 className={s.statNumber}>{stats.total_escalations || 0}</h2>
                <p className={s.statLabel}>Total Escalations</p>
              </CardBody>
            </Card>
            <UncontrolledTooltip placement="bottom" target="stat-total">
              Total number of trips that need attention due to delays
            </UncontrolledTooltip>
          </Col>
          <Col sm={2}>
            <Card className={`${s.statCard} ${s.statPickup} ${s.compactCard}`} id="stat-pickup">
              <CardBody className="text-center">
                <h2 className={s.statNumber}>{stats.pickup_delays || 0}</h2>
                <p className={s.statLabel}>🚚 Pickup Delay</p>
              </CardBody>
            </Card>
            <UncontrolledTooltip placement="bottom" target="stat-pickup">
              <strong>Pickup Delay:</strong> Driver has not started moving towards the pickup location within 15 minutes of scheduled pickup time, OR driver is en route but taking too long to reach pickup
            </UncontrolledTooltip>
          </Col>
          <Col sm={2}>
            <Card className={`${s.statCard} ${s.statDestination} ${s.compactCard}`} id="stat-destination">
              <CardBody className="text-center">
                <h2 className={s.statNumber}>{stats.destination_delays || 0}</h2>
                <p className={s.statLabel}>📍 Drop Reach Delay</p>
              </CardBody>
            </Card>
            <UncontrolledTooltip placement="bottom" target="stat-destination">
              <strong>Destination Delay:</strong> Vehicle has started the trip (loaded goods) but has not reached the drop location within the expected travel time + 20 minutes buffer
            </UncontrolledTooltip>
          </Col>
          <Col sm={2}>
            <Card className={`${s.statCard} ${s.statCompletion} ${s.compactCard}`} id="stat-completion">
              <CardBody className="text-center">
                <h2 className={s.statNumber}>{stats.completion_delays || 0}</h2>
                <p className={s.statLabel}>🏁 Delay in Completing</p>
              </CardBody>
            </Card>
            <UncontrolledTooltip placement="bottom" target="stat-completion">
              <strong>Completion Delay:</strong> Vehicle has reached the drop location but unloading is taking longer than expected (service time + 15 minutes buffer)
            </UncontrolledTooltip>
          </Col>
          <Col sm={2}>
            <Card className={`${s.statCard} ${s.statHigh} ${s.compactCard}`} id="stat-high">
              <CardBody className="text-center">
                <h2 className={s.statNumber}>{stats.high_severity || 0}</h2>
                <p className={s.statLabel}>🔴 High Severity</p>
              </CardBody>
            </Card>
            <UncontrolledTooltip placement="bottom" target="stat-high">
              <strong>High Severity:</strong> Escalations where the delay exceeds 30 minutes - requires immediate attention
            </UncontrolledTooltip>
          </Col>
          <Col sm={2}>
            <Card className={`${s.statCard} ${s.statCompletion} ${s.compactCard}`} id="cancel-stat-total">
              <CardBody className="text-center">
                <h2 className={s.statNumber}>{cancellationStats.total_escalations || 0}</h2>
                <p className={s.statLabel}>Cancel Total</p>
              </CardBody>
            </Card>
            <UncontrolledTooltip placement="bottom" target="cancel-stat-total">
              Total cancellation escalations for the day
            </UncontrolledTooltip>
          </Col>
        </Row>

        <Row>
          <Col sm={12}>
            <Widget
              title={
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mt-0 mb-0">
                    <i className="fa fa-exclamation-triangle text-warning mr-2" />
                    Trip & Cancellation Escalations
                    {currentTime && (
                      <small className="text-muted ml-2">
                        (as of {this.formatTime(currentTime)})
                      </small>
                    )}
                  </h5>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={this.fetchAllEscalations}
                    disabled={isFetching || isFetchingCancellations}
                  >
                    <i className={`fa ${(isFetching || isFetchingCancellations) ? 'fa-spinner fa-spin' : 'fa-refresh'} mr-1`} />
                    Refresh
                  </Button>
                </div>
              }
            >
              {/* Filter Bar */}
              <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: '8px' }}>
                <ButtonGroup size="sm">
                  <Button
                    color={categoryFilter === 'all' ? 'primary' : 'secondary'}
                    onClick={() => this.setState({ categoryFilter: 'all' })}
                    outline={categoryFilter !== 'all'}
                  >
                    All
                  </Button>
                  <Button
                    color={categoryFilter === 'trip' ? 'info' : 'secondary'}
                    onClick={() => this.setState({ categoryFilter: 'trip' })}
                    outline={categoryFilter !== 'trip'}
                  >
                    Trip Delays
                  </Button>
                  <Button
                    color={categoryFilter === 'cancellation' ? 'danger' : 'secondary'}
                    onClick={() => this.setState({ categoryFilter: 'cancellation' })}
                    outline={categoryFilter !== 'cancellation'}
                  >
                    Customer Cancels
                  </Button>
                </ButtonGroup>
                <ButtonGroup size="sm">
                  <Button
                    color={filterType === 'all' ? 'primary' : 'secondary'}
                    onClick={() => this.handleFilterChange('all')}
                    outline={filterType !== 'all'}
                    id="filter-all"
                  >
                    All Delays
                  </Button>
                  <Button
                    color={filterType === 'pickup_delay' ? 'warning' : 'secondary'}
                    onClick={() => this.handleFilterChange('pickup_delay')}
                    outline={filterType !== 'pickup_delay'}
                    id="filter-pickup"
                  >
                    🚚 Pickup Delay
                  </Button>
                  <Button
                    color={filterType === 'destination_delay' ? 'danger' : 'secondary'}
                    onClick={() => this.handleFilterChange('destination_delay')}
                    outline={filterType !== 'destination_delay'}
                    id="filter-destination"
                  >
                    📍 Drop Delay
                  </Button>
                  <Button
                    color={filterType === 'completion_delay' ? 'info' : 'secondary'}
                    onClick={() => this.handleFilterChange('completion_delay')}
                    outline={filterType !== 'completion_delay'}
                    id="filter-completion"
                  >
                    🏁 Completion Delay
                  </Button>
                  <Button
                    style={filterType === 'enroute_alert' ? { backgroundColor: '#7c3aed', color: '#fff', borderColor: '#7c3aed' } : {}}
                    color={filterType === 'enroute_alert' ? undefined : 'secondary'}
                    onClick={() => this.handleFilterChange('enroute_alert')}
                    outline={filterType !== 'enroute_alert'}
                    id="filter-enroute"
                  >
                    🚗 Enroute Alert
                  </Button>
                  <Button
                    style={filterType === 'idle_driver' ? { backgroundColor: '#059669', color: '#fff', borderColor: '#059669' } : {}}
                    color={filterType === 'idle_driver' ? undefined : 'secondary'}
                    onClick={() => this.handleFilterChange('idle_driver')}
                    outline={filterType !== 'idle_driver'}
                    id="filter-idle"
                  >
                    🏠 Idle Driver
                  </Button>
                </ButtonGroup>
                <ButtonGroup size="sm">
                  <Button
                    color={cancellationFilter === 'pending' ? 'warning' : 'secondary'}
                    onClick={() => this.handleCancellationFilterChange('pending')}
                    outline={cancellationFilter !== 'pending'}
                  >
                    Pending Cancel Checks
                  </Button>
                  <Button
                    color={cancellationFilter === 'resolved' ? 'success' : 'secondary'}
                    onClick={() => this.handleCancellationFilterChange('resolved')}
                    outline={cancellationFilter !== 'resolved'}
                  >
                    Resolved Cancel Checks
                  </Button>
                  <Button
                    color={cancellationFilter === 'all' ? 'primary' : 'secondary'}
                    onClick={() => this.handleCancellationFilterChange('all')}
                    outline={cancellationFilter !== 'all'}
                  >
                    All Cancel Checks
                  </Button>
                </ButtonGroup>
              </div>
              {/* Search Bar */}
              <Row className="mb-3">
                <Col sm={6}>
                  <InputGroup>
                    <Input 
                      placeholder="Search by booking ID, driver, customer, vehicle, or location..." 
                      value={searchTerm}
                      onChange={this.handleSearch}
                    />
                    <InputGroupAddon addonType="append">
                      <Button color="default">
                        <i className="fa fa-search" />
                      </Button>
                    </InputGroupAddon>
                  </InputGroup>
                </Col>
                <Col sm={6} className="text-right">
                  <div className="text-muted">
                    <small>
                      Trip: <strong>{escalations.length}</strong> | Cancels: <strong>{cancellationEscalations.length}</strong> | 
                      Showing: <strong>{combinedEscalations.length}</strong>
                      <span className="ml-2 text-success">
                        <i className="fa fa-circle fa-xs mr-1" />
                        Auto-refresh: 30s
                      </span>
                    </small>
                  </div>
                </Col>
              </Row>

              {/* Error State */}
              {(error || cancellationError) && (
                <Card className="mb-3">
                  <CardBody className="text-center py-4">
                    <i className="fa fa-exclamation-circle fa-3x text-danger mb-3" />
                    <h5 className="text-danger">Error Loading Escalations</h5>
                    <p className="text-muted">{error || cancellationError}</p>
                    <Button color="primary" onClick={this.fetchAllEscalations}>
                      <i className="fa fa-refresh mr-1" /> Retry
                    </Button>
                  </CardBody>
                </Card>
              )}

              {/* Empty State */}
              {!error && !cancellationError && combinedEscalations.length === 0 && !isFetching && !isFetchingCancellations && (
                <Card>
                  <CardBody className="text-center py-5">
                    <i className="fa fa-check-circle fa-3x text-success mb-3" />
                    <h5 className="text-muted">No Escalations Found</h5>
                    <p className="text-muted">
                      {searchTerm 
                        ? 'Try adjusting your search criteria' 
                        : 'All trips are running on schedule! 🎉'}
                    </p>
                  </CardBody>
                </Card>
              )}

              {/* Escalations Table */}
              {!error && !cancellationError && (combinedEscalations.length > 0 || isFetching || isFetchingCancellations) && (
                <div className="table-responsive">
                  <Table hover className={s.escalationTable}>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Severity / Status</th>
                        <th>Booking</th>
                        <th>Driver / Customer</th>
                        <th>Vehicle</th>
                        <th>Route / Pickup</th>
                        <th>Time</th>
                        <th>Overdue / Age</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isFetching || isFetchingCancellations) && combinedEscalations.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <i className="fa fa-spinner fa-spin fa-2x text-primary mb-2" />
                            <p className="text-muted mb-0">Loading escalations...</p>
                          </td>
                        </tr>
                      ) : (
                        combinedEscalations.map((escalation, index) => (
                          <tr 
                            key={`${escalation.trip_id || escalation.id}-${index}`}
                            className={`${escalation.severity === 'high' || (!escalation.resolved && escalation._kind === 'cancellation') ? s.highSeverityRow : ''} ${s.clickableRow}`}
                            onClick={() => {
                              if (escalation._isIdleDriverAlert) {
                                this.setState({ selectedIdleDriver: escalation, showIdleDetailModal: true });
                              } else {
                                this.setState({ selectedEscalation: escalation, showDetailModal: true });
                              }
                            }}
                          >
                            <td>
                              {escalation._isIdleDriverAlert ? (
                                <Badge style={{ backgroundColor: '#059669', color: '#fff' }} className={s.typeBadge}>🏠 Idle Driver</Badge>
                              ) : escalation._isEnrouteAlert ? (
                                <Badge style={{ backgroundColor: '#7c3aed', color: '#fff' }} className={s.typeBadge}>🚗 Enroute Alert</Badge>
                              ) : escalation._isPickupDelayAlert ? (
                                <Badge style={{ backgroundColor: '#d97706', color: '#fff' }} className={s.typeBadge}>🚚 Pickup Delay</Badge>
                              ) : escalation._isDropDelayAlert ? (
                                <Badge style={{ backgroundColor: '#dc2626', color: '#fff' }} className={s.typeBadge}>📍 Drop Delay</Badge>
                              ) : escalation._kind === 'trip' ? (
                                this.getTypeBadge(escalation.escalation_type)
                              ) : (
                                <Badge color="danger" className={s.typeBadge}>🚫 Cancellation</Badge>
                              )}
                            </td>
                            <td>
                              {escalation._isIdleDriverAlert ? (() => {
                                if (escalation._isAtBase) return <Badge color="success" className={s.severityBadge}>🏠 At Base</Badge>;
                                if (escalation._isNotReachedBase) return <Badge color="danger" className={s.severityBadge}>🔴 Not Reached</Badge>;
                                if (escalation._isReturning) return <Badge color="info" className={s.severityBadge}>🔵 Returning</Badge>;
                                return <Badge color="warning" className={s.severityBadge}>🟡 Idle</Badge>;
                              })()
                              : escalation._kind === 'trip'
                                ? this.getSeverityBadge(escalation.severity)
                                : (escalation.resolved
                                  ? <Badge color="success" className={s.severityBadge}>Resolved</Badge>
                                  : <Badge color="warning" className={s.severityBadge}>Pending</Badge>)}
                            </td>
                            <td>
                              <div>
                                {escalation._isIdleDriverAlert ? (
                                  <>
                                    <strong className={s.bookingId}>—</strong>
                                    <br />
                                    <small className="text-muted">Last: #{escalation.last_trip_id || '—'}</small>
                                  </>
                                ) : (
                                  <>
                                    <strong className={s.bookingId}>{escalation.booking_id}</strong>
                                    <br />
                                    <small className="text-muted">Trip #{escalation.trip_id || escalation.id}</small>
                                  </>
                                )}
                              </div>
                            </td>
                            <td>
                              {escalation._isIdleDriverAlert ? (
                                <div>
                                  <strong>{escalation.driver_name || `Vehicle #${escalation.vehicle_id}`}</strong>
                                  <br />
                                  <a
                                    href={`tel:${escalation.phone_number}`}
                                    className={s.phoneLink}
                                    onClick={(e) => {
                                      if (!escalation.phone_number || escalation.phone_number === 'N/A') {
                                        e.preventDefault();
                                        toast.warning('Phone number not available');
                                      }
                                    }}
                                  >
                                    <i className="fa fa-phone mr-1" />
                                    {escalation.phone_number || 'N/A'}
                                  </a>
                                </div>
                              ) : escalation._kind === 'trip' ? (
                                <div>
                                  <strong>{escalation.driver_name}</strong>
                                  <br />
                                  <a 
                                    href={`tel:${escalation.driver_phone}`}
                                    className={s.phoneLink}
                                    onClick={(e) => {
                                      if (!escalation.driver_phone || escalation.driver_phone === 'N/A') {
                                        e.preventDefault();
                                        toast.warning('Phone number not available');
                                      }
                                    }}
                                  >
                                    <i className="fa fa-phone mr-1" />
                                    {escalation.driver_phone}
                                  </a>
                                </div>
                              ) : (
                                <div>
                                  <strong>{getCustomerDisplayName(escalation.customer_name, escalation.customer_business_name, escalation.customer_phone, 'N/A')}</strong>
                                  <br />
                                  <a 
                                    href={`tel:${escalation.customer_phone}`}
                                    className={s.phoneLink}
                                    onClick={(e) => {
                                      if (!escalation.customer_phone) {
                                        e.preventDefault();
                                        toast.warning('Phone number not available');
                                      }
                                    }}
                                  >
                                    <i className="fa fa-phone mr-1" />
                                    {escalation.customer_phone || 'N/A'}
                                  </a>
                                </div>
                              )}
                            </td>
                            <td>
                              <div>
                                <strong>{escalation.vehicle_id}</strong>
                                <br />
                                <small className="text-muted">{escalation.vehicle_registration}</small>
                              </div>
                            </td>
                            <td className={s.routeCell}>
                              <div className={s.routeInfo}>
                                {escalation._isIdleDriverAlert ? (
                                  <div className={s.pickup}>
                                    <span className={s.locationIcon}>📍</span>
                                    <span className={s.locationText}>No active route</span>
                                  </div>
                                ) : (
                                  <>
                                <div className={s.pickup}>
                                  <span className={s.locationIcon}>📍</span>
                                  <span className={s.locationText}>
                                    {String(escalation.pickup_location || 'Unknown').substring(0, 25)}
                                    {String(escalation.pickup_location || '').length > 25 ? '...' : ''}
                                  </span>
                                </div>
                                {escalation._kind === 'trip' && !escalation._isIdleDriverAlert && (
                                  <>
                                    <div className={s.routeArrow}>↓</div>
                                    <div className={s.drop}>
                                      <span className={s.locationIcon}>🎯</span>
                                      <span className={s.locationText}>
                                        {(escalation.drop_location || 'Unknown').substring(0, 25)}
                                        {(escalation.drop_location || '').length > 25 ? '...' : ''}
                                      </span>
                                    </div>
                                  </>
                                )}
                                  </>
                                )}
                              </div>
                            </td>
                            <td>
                              <div>
                                {escalation._isIdleDriverAlert ? (
                                  <>
                                    <small className="text-muted">Idle Since:</small>
                                    <br />
                                    <strong>{escalation.idle_since ? new Date(escalation.idle_since).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</strong>
                                  </>
                                ) : escalation._kind === 'trip' ? (
                                  escalation.escalation_type === 'pickup_delay' ? (
                                    <>
                                      <small className="text-muted">Pickup:</small>
                                      <br />
                                      <strong>{this.formatTime(escalation.scheduled_pickup_time)}</strong>
                                    </>
                                  ) : (
                                    <>
                                      <small className="text-muted">Started:</small>
                                      <br />
                                      <strong>{this.formatTime(escalation.trip_started_at)}</strong>
                                    </>
                                  )
                                ) : (
                                  <>
                                    <small className="text-muted">Cancelled:</small>
                                    <br />
                                    <strong>{this.formatTime(escalation.created_at)}</strong>
                                  </>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className={`${s.overdueTime} ${escalation.severity === 'high' ? s.overdueHigh : s.overdueMedium}`}>
                                <strong>
                                  {escalation._isIdleDriverAlert
                                    ? (escalation.idle_since ? this.formatOverdue(Math.round((Date.now() - new Date(escalation.idle_since).getTime()) / 60000)) : '—')
                                    : escalation._kind === 'trip'
                                    ? this.formatOverdue(escalation.time_overdue_minutes)
                                    : escalation.minutes_since_escalation
                                      ? `${Math.round(escalation.minutes_since_escalation)} min`
                                      : '-'}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div className={s.actionButtons} onClick={e => e.stopPropagation()}>
                                {!escalation._isIdleDriverAlert && (
                                <Button
                                  color="success"
                                  size="sm"
                                  className={s.callBtn}
                                  onClick={() => this.handleCallDriver(escalation.driver_phone)}
                                  title="Call Driver"
                                >
                                  <i className="fa fa-phone" />
                                </Button>
                                )}
                                {escalation._isIdleDriverAlert ? (
                                  <>
                                    <Button
                                      color="success"
                                      size="sm"
                                      className={s.callBtn}
                                      onClick={() => this.handleCallDriver(escalation.phone_number)}
                                      title="Call Driver"
                                    >
                                      <i className="fa fa-phone" />
                                    </Button>
                                    <Button
                                      style={{ backgroundColor: '#059669', borderColor: '#059669', color: '#fff', fontWeight: '600' }}
                                      size="sm"
                                      onClick={() => this.setState({ selectedIdleDriver: escalation, showIdleDetailModal: true })}
                                      title="Ops Agent - Idle Driver Details"
                                    >
                                      🤖 Ops Agent
                                    </Button>
                                  </>
                                ) : escalation._isEnrouteAlert ? (
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => this.setState({ selectedEnrouteAlert: escalation, showEnrouteModal: true })}
                                    title="Ops Agent - View Movement Alert"
                                    style={{ fontWeight: '600' }}
                                  >
                                    🤖 Ops Agent
                                  </Button>
                                ) : escalation._isPickupDelayAlert ? (
                                  <Button
                                    style={{ backgroundColor: '#d97706', borderColor: '#d97706', fontWeight: '600' }}
                                    size="sm"
                                    onClick={() => this.setState({ selectedPickupAlert: escalation, showPickupDelayModal: true })}
                                    title="Ops Agent - Pickup Delay Alert"
                                  >
                                    🤖 Ops Agent
                                  </Button>
                                ) : escalation._isDropDelayAlert ? (
                                  <Button
                                    style={{ backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff', fontWeight: '600' }}
                                    size="sm"
                                    onClick={() => this.setState({ selectedDropAlert: escalation, showDropDelayModal: true })}
                                    title="Ops Agent - Drop Delay Alert"
                                  >
                                    🤖 Ops Agent
                                  </Button>
                                ) : escalation._kind === 'trip' ? (
                                  <Link 
                                    to={`/app/trips/${escalation.trip_id}`}
                                    className="btn btn-primary btn-sm"
                                    title="View Trip Details"
                                  >
                                    <i className="fa fa-eye" />
                                  </Link>
                                ) : (
                                  (!escalation.resolved ? (
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => this.handleResolveCancellation(escalation.id)}
                                      disabled={resolvingId === escalation.id}
                                      title="Resolve Escalation"
                                    >
                                      {resolvingId === escalation.id ? (
                                        <i className="fa fa-spinner fa-spin" />
                                      ) : (
                                        <i className="fa fa-check" />
                                      )}
                                    </Button>
                                  ) : (
                                    <Badge color="success" className="py-1 px-2">
                                      <i className="fa fa-check mr-1" />
                                      Resolved
                                    </Badge>
                                  ))
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Widget>
          </Col>
        </Row>

        {/* Legend */}
        <Row>
          <Col>
            <Card className={s.legendCard}>
              <CardBody className="py-2">
                <small className="text-muted">
                  <strong>Legend:</strong>
                  <span className="ml-3">🚚 Pickup Delay - Driver hasn't reached pickup on time</span>
                  <span className="ml-3">🏁 Completion Delay - Trip taking longer than expected</span>
                  <span className="ml-3">🚫 Cancellation - Customer cancelled after assignment</span>
                  <span className="ml-3">🔴 High - More than 30 min overdue / pending cancel</span>
                  <span className="ml-3">🟡 Medium - Less than 30 min overdue</span>
                </small>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Detail Modal */}
        <Modal isOpen={showDetailModal} toggle={() => this.setState({ showDetailModal: false, selectedEscalation: null })} size="lg">
          <ModalHeader toggle={() => this.setState({ showDetailModal: false, selectedEscalation: null })}>
            {selectedEscalation?._kind === 'cancellation' ? 'Customer Cancellation' : 'Trip Escalation'}
          </ModalHeader>
          <ModalBody>
            {selectedEscalation && (
              <div className={s.detailGrid}>
                <div className={s.detailSection}>
                  <h5>Booking</h5>
                  <p className={s.detailValue}>#{selectedEscalation.booking_id}</p>
                  <p className={s.detailSub}>Trip #{selectedEscalation.trip_id || selectedEscalation.id}</p>
                </div>
                <div className={s.detailSection}>
                  <h5>{selectedEscalation._kind === 'cancellation' ? 'Customer' : 'Driver'}</h5>
                  <p className={s.detailValue}>
                    {selectedEscalation._kind === 'cancellation' ? (getCustomerDisplayName(selectedEscalation.customer_name, selectedEscalation.customer_business_name, selectedEscalation.customer_phone, 'N/A')) : (selectedEscalation.driver_name || 'N/A')}
                  </p>
                  <p className={s.detailSub}>
                    {selectedEscalation._kind === 'cancellation' ? (selectedEscalation.customer_phone || 'N/A') : (selectedEscalation.driver_phone || 'N/A')}
                  </p>
                </div>
                <div className={s.detailSection}>
                  <h5>Vehicle</h5>
                  <p className={s.detailValue}>{selectedEscalation.vehicle_id || 'N/A'}</p>
                  <p className={s.detailSub}>{selectedEscalation.vehicle_registration || ''}</p>
                </div>
                <div className={s.detailSection}>
                  <h5>Route</h5>
                  <p className={s.detailSub}>Pickup: {selectedEscalation.pickup_location || 'N/A'}</p>
                  {selectedEscalation._kind === 'trip' && (
                    <p className={s.detailSub}>Drop: {selectedEscalation.drop_location || 'N/A'}</p>
                  )}
                </div>
                <div className={s.detailSection}>
                  <h5>Time</h5>
                  <p className={s.detailSub}>
                    {selectedEscalation._kind === 'trip'
                      ? `Scheduled: ${this.formatTime(selectedEscalation.scheduled_pickup_time) || 'N/A'}`
                      : `Cancelled: ${this.formatTime(selectedEscalation.created_at) || 'N/A'}`}
                  </p>
                  {selectedEscalation._kind === 'trip' && (
                    <p className={s.detailSub}>Started: {this.formatTime(selectedEscalation.trip_started_at) || 'N/A'}</p>
                  )}
                  {selectedEscalation._kind === 'cancellation' && selectedEscalation.minutes_since_escalation && (
                    <p className={s.detailSub}>{Math.round(selectedEscalation.minutes_since_escalation)} min ago</p>
                  )}
                </div>
                <div className={s.detailSectionWide}>
                  <h5>Escalation Reason</h5>
                  <p className={s.detailValue}>
                    {selectedEscalation.escalation_reason ||
                     selectedEscalation.reason ||
                     selectedEscalation.driver_status ||
                     selectedEscalation.resolution_notes ||
                     'Not provided'}
                  </p>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => this.setState({ showDetailModal: false, selectedEscalation: null })}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* ── Enroute Movement Alert Modal (Ops Agent) ─────────── */}
        <Modal
          isOpen={showEnrouteModal}
          toggle={() => this.setState({ showEnrouteModal: false, selectedEnrouteAlert: null })}
          size="md"
        >
          <ModalHeader
            toggle={() => this.setState({ showEnrouteModal: false, selectedEnrouteAlert: null })}
            style={{ background: '#fff3cd', borderBottom: '2px solid #ffc107' }}
          >
            🚨 Enroute Movement Alert
          </ModalHeader>
          <ModalBody>
            {selectedEnrouteAlert && (
              <div>
                {/* Reason */}
                <div style={{ background: '#fff3cd', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #ffc107' }}>
                  <strong>📋 Reason:</strong>
                  <p style={{ margin: '8px 0 0', fontSize: '1em' }}>
                    {selectedEnrouteAlert.reason || 'Driver movement anomaly detected'}
                  </p>
                </div>

                {/* Suggested Action */}
                <div style={{ background: '#d1ecf1', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #bee5eb' }}>
                  <strong>💡 Suggested Action:</strong>
                  <p style={{ margin: '8px 0 0' }}>
                    {selectedEnrouteAlert.suggested_action || 'Please check and contact the driver'}
                  </p>
                </div>

                {/* Driver Info */}
                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', border: '1px solid #dee2e6' }}>
                  <h6 style={{ marginBottom: '12px' }}>👤 Driver Info</h6>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600', width: '130px' }}>Name:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedEnrouteAlert.driver_name || 'Unknown'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Phone:</td>
                        <td style={{ padding: '4px 8px' }}>
                          <a href={`tel:${selectedEnrouteAlert.driver_phone}`}>
                            {selectedEnrouteAlert.driver_phone || 'N/A'}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Vehicle:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedEnrouteAlert.vehicle_registration || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Trip ID:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedEnrouteAlert.trip_id}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Booking ID:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedEnrouteAlert.booking_id}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Moved:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedEnrouteAlert.moved_km} km in {selectedEnrouteAlert.elapsed_minutes} min</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Distance to Pickup:</td>
                        <td style={{ padding: '4px 8px' }}>
                          {selectedEnrouteAlert.initial_distance_km} km → {selectedEnrouteAlert.current_distance_km} km
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="success"
              onClick={() => this.handleCallDriver(selectedEnrouteAlert?.driver_phone)}
            >
              📞 Call Driver
            </Button>
            <Button
              color="primary"
              disabled={sendingMessage}
              onClick={() => this.handleSendMessageWithConfirm(
                selectedEnrouteAlert?.trip_id,
                `You haven't moved toward the pickup location for ${this.formatMinutesForMessage(selectedEnrouteAlert?.elapsed_minutes)} minutes. Please proceed immediately.`
              )}
            >
              {sendingMessage ? (
                <><i className="fa fa-spinner fa-spin mr-1" />Sending...</>
              ) : (
                this.state.sentMessages.has(String(selectedEnrouteAlert?.trip_id)) ? '💬 Resend Message' : '💬 Send Message'
              )}
            </Button>
            <Button
              color="danger"
              disabled={this.state.cancellingTrip}
              onClick={() => this.handleDismissClick('alert', selectedEnrouteAlert, 'enroute', 'showEnrouteModal', 'selectedEnrouteAlert', 'enrouteAlerts')}
            >
              {this.state.cancellingTrip ? (
                <><i className="fa fa-spinner fa-spin mr-1" />Dismissing...</>
              ) : (
                '✕ Dismiss'
              )}
            </Button>
            <Button
              color="secondary"
              onClick={() => this.setState({ showEnrouteModal: false, selectedEnrouteAlert: null })}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* ── Pickup Delay Alert Modal (Ops Agent) ─────────── */}
        <Modal
          isOpen={showPickupDelayModal}
          toggle={() => this.setState({ showPickupDelayModal: false, selectedPickupAlert: null })}
          size="lg"
        >
          <ModalHeader
            toggle={() => this.setState({ showPickupDelayModal: false, selectedPickupAlert: null })}
            style={{ background: '#fef3c7', borderBottom: '2px solid #d97706' }}
          >
            📦 Pickup Delay Alert — Ops Agent
          </ModalHeader>
          <ModalBody>
            {selectedPickupAlert && (
              <div>
                {/* Driver Side */}
                <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #d97706' }}>
                  <h6 style={{ marginBottom: '12px', color: '#92400e' }}>🚗 Driver Side</h6>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600', width: '160px' }}>Driver Name:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedPickupAlert.driver?.name || 'Unknown'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Phone:</td>
                        <td style={{ padding: '4px 8px' }}>
                          <a href={`tel:${selectedPickupAlert.driver?.phone}`}>
                            {selectedPickupAlert.driver?.phone || 'N/A'}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Vehicle ID:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedPickupAlert.driver?.vehicle_id || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Vehicle Reg:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedPickupAlert.driver?.vehicle_registration || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Accepted At:</td>
                        <td style={{ padding: '4px 8px' }}>{this.formatTime(selectedPickupAlert.driver?.accepted_at) || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Pickup Time:</td>
                        <td style={{ padding: '4px 8px' }}>{this.formatTime(selectedPickupAlert.pickup_time) || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Overdue:</td>
                        <td style={{ padding: '4px 8px', color: '#dc2626', fontWeight: '600' }}>{selectedPickupAlert.time_overdue_minutes || 0} min</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="mt-2" style={{ background: '#fffbeb', borderRadius: '6px', padding: '8px 12px', border: '1px dashed #d97706', fontSize: '0.9em' }}>
                    <strong>📨 Message:</strong> {selectedPickupAlert.driver_message}
                  </div>
                  <div className="mt-2 d-flex" style={{ gap: '8px' }}>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => this.handleCallDriver(selectedPickupAlert.driver?.phone)}
                    >
                      📞 Call Driver
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      disabled={sendingMessage}
                      onClick={() => this.handleSendMessageWithConfirm(
                        selectedPickupAlert.trip_id,
                        selectedPickupAlert.driver_message
                      )}
                    >
                      {sendingMessage ? <><i className="fa fa-spinner fa-spin mr-1" />Sending...</> : (this.state.sentMessages.has(String(selectedPickupAlert?.trip_id)) ? '💬 Resend Message' : '💬 Send Message')}
                    </Button>
                  </div>
                </div>

                {/* Customer Side */}
                <div style={{ background: '#dbeafe', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #3b82f6' }}>
                  <h6 style={{ marginBottom: '12px', color: '#1e40af' }}>👤 Customer Side</h6>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600', width: '160px' }}>Customer Name:</td>
                        <td style={{ padding: '4px 8px' }}>{getCustomerDisplayName(selectedPickupAlert.customer?.name, selectedPickupAlert.customer?.business_name, selectedPickupAlert.customer?.phone, 'Unknown')}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Phone:</td>
                        <td style={{ padding: '4px 8px' }}>
                          <a href={`tel:${selectedPickupAlert.customer?.phone}`}>
                            {selectedPickupAlert.customer?.phone || 'N/A'}
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="mt-2" style={{ background: '#eff6ff', borderRadius: '6px', padding: '8px 12px', border: '1px dashed #3b82f6', fontSize: '0.9em' }}>
                    <strong>📨 Message:</strong> {selectedPickupAlert.customer_message}
                  </div>
                  <div className="mt-2 d-flex" style={{ gap: '8px' }}>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => this.handleCallDriver(selectedPickupAlert.customer?.phone)}
                    >
                      📞 Call Customer
                    </Button>
                    <Button
                      color="info"
                      size="sm"
                      disabled={this.state.sendingMessage}
                      onClick={() => this.handleSendMessageWithConfirm(selectedPickupAlert.trip_id, selectedPickupAlert.customer_message)}
                    >
                      {this.state.sendingMessage ? <><i className="fa fa-spinner fa-spin mr-1" />Sending...</> : (this.state.sentMessages.has(String(selectedPickupAlert?.trip_id)) ? '💬 Resend Message' : '💬 Send Message')}
                    </Button>
                  </div>
                </div>

                {/* Trip Info */}
                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px 16px', border: '1px solid #dee2e6' }}>
                  <div className="d-flex justify-content-between">
                    <span><strong>Trip #{selectedPickupAlert.trip_id}</strong> &nbsp; Booking #{selectedPickupAlert.booking_id}</span>
                    <Badge color={selectedPickupAlert.severity === 'high' ? 'danger' : 'warning'}>
                      {selectedPickupAlert.severity === 'high' ? '🔴 High' : '🟡 Medium'}
                    </Badge>
                  </div>
                  <small className="text-muted">
                    📍 {selectedPickupAlert.pickup_location} → 🎯 {selectedPickupAlert.drop_location}
                  </small>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              disabled={this.state.cancellingTrip}
              onClick={() => this.handleDismissClick('alert', selectedPickupAlert, 'pickup_delay', 'showPickupDelayModal', 'selectedPickupAlert', 'pickupDelayAlerts')}
            >
              {this.state.cancellingTrip ? (
                <><i className="fa fa-spinner fa-spin mr-1" />Dismissing...</>
              ) : (
                '✕ Dismiss'
              )}
            </Button>
            <Button
              color="secondary"
              onClick={() => this.setState({ showPickupDelayModal: false, selectedPickupAlert: null })}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* ── Drop Delay Alert Modal (Ops Agent) ─────────── */}
        <Modal
          isOpen={showDropDelayModal}
          toggle={() => this.setState({ showDropDelayModal: false, selectedDropAlert: null })}
          size="md"
        >
          <ModalHeader
            toggle={() => this.setState({ showDropDelayModal: false, selectedDropAlert: null })}
            style={{ background: '#fee2e2', borderBottom: '2px solid #dc2626' }}
          >
            🛑 Drop Delay Alert — Ops Agent
          </ModalHeader>
          <ModalBody>
            {selectedDropAlert && (
              <div>
                {/* Reason */}
                <div style={{ background: '#fee2e2', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #dc2626' }}>
                  <strong>📋 Reason:</strong>
                  <p style={{ margin: '8px 0 0', fontSize: '1em' }}>
                    {selectedDropAlert.reason || 'Driver not moving after loading'}
                  </p>
                </div>

                {/* Suggested Action */}
                <div style={{ background: '#d1ecf1', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #bee5eb' }}>
                  <strong>💡 Suggested Action:</strong>
                  <p style={{ margin: '8px 0 0' }}>
                    {selectedDropAlert.suggested_action || 'Contact the driver and check status'}
                  </p>
                </div>

                {/* Driver Info */}
                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', border: '1px solid #dee2e6' }}>
                  <h6 style={{ marginBottom: '12px' }}>👤 Driver Info</h6>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600', width: '130px' }}>Name:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedDropAlert.driver_name || 'Unknown'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Phone:</td>
                        <td style={{ padding: '4px 8px' }}>
                          <a href={`tel:${selectedDropAlert.driver_phone}`}>
                            {selectedDropAlert.driver_phone || 'N/A'}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Vehicle:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedDropAlert.vehicle_registration || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Trip ID:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedDropAlert.trip_id}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Booking ID:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedDropAlert.booking_id}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Moved:</td>
                        <td style={{ padding: '4px 8px' }}>{selectedDropAlert.moved_km} km in {selectedDropAlert.elapsed_minutes} min</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 8px', fontWeight: '600' }}>Distance to Drop:</td>
                        <td style={{ padding: '4px 8px' }}>
                          {selectedDropAlert.initial_distance_km} km → {selectedDropAlert.current_distance_km} km
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="success"
              onClick={() => this.handleCallDriver(selectedDropAlert?.driver_phone)}
            >
              📞 Call Driver
            </Button>
            <Button
              color="primary"
              disabled={sendingMessage}
              onClick={() => this.handleSendMessageWithConfirm(
                selectedDropAlert?.trip_id,
                `You have been at the pickup/loading location for ${this.formatMinutesForMessage(selectedDropAlert?.elapsed_minutes)} minutes after loading. Please proceed to the drop location.`
              )}
            >
              {sendingMessage ? (
                <><i className="fa fa-spinner fa-spin mr-1" />Sending...</>
              ) : (
                this.state.sentMessages.has(String(selectedDropAlert?.trip_id)) ? '💬 Resend Message' : '💬 Send Message'
              )}
            </Button>
            <Button
              color="danger"
              disabled={this.state.cancellingTrip}
              onClick={() => this.handleDismissClick('alert', selectedDropAlert, 'drop_delay', 'showDropDelayModal', 'selectedDropAlert', 'dropDelayAlerts')}
            >
              {this.state.cancellingTrip ? (
                <><i className="fa fa-spinner fa-spin mr-1" />Dismissing...</>
              ) : (
                '✕ Dismiss'
              )}
            </Button>
            <Button
              color="secondary"
              onClick={() => this.setState({ showDropDelayModal: false, selectedDropAlert: null })}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* ── Idle Driver Detail Modal (Ops Agent) ──────────────────── */}
        <Modal
          isOpen={this.state.showIdleDetailModal}
          toggle={() => this.setState({ showIdleDetailModal: false, selectedIdleDriver: null })}
          size="md"
        >
          <ModalHeader
            toggle={() => this.setState({ showIdleDetailModal: false, selectedIdleDriver: null })}
            style={{ background: '#d1fae5', borderBottom: '2px solid #059669' }}
          >
            🏠 Idle Driver — Return to Base
          </ModalHeader>
          <ModalBody>
            {this.state.selectedIdleDriver && (() => {
              const driver = this.state.selectedIdleDriver;
              const rtbState = driver._rtbState || driver.rtb_state || 'IDLE';
              const isAtBase = driver._isAtBase;
              const isReturning = driver._isReturning;
              const isNotReachedBase = driver._isNotReachedBase;

              let borderColor, bgColor, badgeColor, badgeText;
              if (isAtBase) {
                borderColor = '#28a745'; bgColor = '#d4edda'; badgeColor = 'success'; badgeText = '🏠 At Base';
              } else if (isNotReachedBase) {
                borderColor = '#dc3545'; bgColor = '#f8d7da'; badgeColor = 'danger'; badgeText = '🔴 Not Reached Base';
              } else if (isReturning) {
                borderColor = '#007bff'; bgColor = '#cce5ff'; badgeColor = 'info'; badgeText = '🔵 Returning';
              } else {
                borderColor = '#ffc107'; bgColor = '#fff3cd'; badgeColor = 'warning'; badgeText = 'Idle';
              }

              const showRTBButton = !isAtBase && !isReturning && !isNotReachedBase;

              return (
                <Card style={{ border: `1px solid ${borderColor}` }}>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h5 className="mb-1">
                          🚗 Vehicle #{driver.vehicle_id || driver.driver_id}
                          <Badge color={badgeColor} className="ml-2">{badgeText}</Badge>
                        </h5>
                        <p className="mb-1 text-muted">
                          <strong>Phone:</strong> {driver.phone_number || 'N/A'}
                        </p>
                        {driver.last_trip_id && (
                          <p className="mb-1 text-muted">
                            <strong>Last Trip:</strong> #{driver.last_trip_id}
                          </p>
                        )}
                        {driver.idle_since && (
                          <p className="mb-1 text-muted">
                            <strong>Idle Since:</strong> {new Date(driver.idle_since).toLocaleTimeString()}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div style={{
                          background: bgColor,
                          border: `1px solid ${borderColor}`,
                          borderRadius: '8px',
                          padding: '8px 12px',
                          marginBottom: '10px',
                          fontSize: '0.85em',
                        }}>
                          <strong>🤖 Agent Reason:</strong><br />
                          {driver.reason || driver.escalation_reason || 'No bookings available, send driver to base'}
                        </div>
                        {/* Driver's location — yellow card shows last drop + current; others show current only */}
                        {(() => {
                          const loc = driver.location || driver;
                          const lat = loc?.lat || loc?.latitude;
                          const lng = loc?.lng || loc?.longitude;
                          if (!lat || !lng) return null;
                          const cacheKey = `${lat},${lng}`;
                          if (!this.state.idleLocationNames[cacheKey] && !this.state.idleLocationLoading[cacheKey]) {
                            setTimeout(() => this.reverseGeocodeLocation(lat, lng), 0);
                          }
                          const locationName = this.state.idleLocationNames[cacheKey];
                          const isLoading = this.state.idleLocationLoading[cacheKey];
                          const lastDrop = driver.last_drop_location;
                          return (
                            <div style={{
                              background: '#f0f9ff',
                              border: '1px solid #bae6fd',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '0.82em',
                              marginTop: '6px',
                            }}>
                              {lastDrop && (
                                <div><strong>📦 Last Drop:</strong> {lastDrop.split(',')[0]}</div>
                              )}
                              <div>
                                <strong>📍 Currently:</strong>{' '}
                                {isLoading ? (
                                  <><i className="fa fa-spinner fa-spin" /> Loading...</>
                                ) : (
                                  locationName || `${lat}, ${lng}`
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <Button
              color="success"
              onClick={() => this.handleCallDriver(this.state.selectedIdleDriver?.phone_number)}
            >
              📞 Call Driver
            </Button>
            {this.state.selectedIdleDriver && !this.state.selectedIdleDriver._isAtBase && !this.state.selectedIdleDriver._isReturning && !this.state.selectedIdleDriver._isNotReachedBase && (
              <Button
                color="danger"
                disabled={this.state.sendingReturnToBase === this.state.selectedIdleDriver?.driver_id}
                onClick={() => this.handleReturnToBase(this.state.selectedIdleDriver?.driver_id)}
                style={{ fontWeight: '600' }}
              >
                {this.state.sendingReturnToBase === this.state.selectedIdleDriver?.driver_id ? (
                  <><i className="fa fa-spinner fa-spin mr-1" /> Sending...</>
                ) : (
                  '🏠 Return to Base'
                )}
              </Button>
            )}
            <Button
              color="warning"
              disabled={this.state.cancellingTrip}
              onClick={() => this.handleDismissClick('idle', this.state.selectedIdleDriver)}
            >
              {this.state.cancellingTrip ? (
                <><i className="fa fa-spinner fa-spin mr-1" />Dismissing...</>
              ) : (
                '✕ Dismiss'
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ── Dismiss Reason Prompt ── */}
        <Modal
          isOpen={!!this.state.dismissPending}
          toggle={() => this.setState({ dismissPending: null, dismissReason: '' })}
          size="sm"
          centered
        >
          <ModalHeader toggle={() => this.setState({ dismissPending: null, dismissReason: '' })}>
            Dismiss — Enter Reason
          </ModalHeader>
          <ModalBody>
            <input
              type="text"
              className="form-control"
              placeholder="Enter reason and press Enter..."
              value={this.state.dismissReason}
              autoFocus
              onChange={e => this.setState({ dismissReason: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter' && this.state.dismissReason.trim()) {
                  this.handleDismissConfirm();
                }
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              size="sm"
              onClick={() => this.setState({ dismissPending: null, dismissReason: '' })}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              size="sm"
              disabled={!this.state.dismissReason.trim()}
              onClick={this.handleDismissConfirm}
            >
              Dismiss
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

// HOC to inject city context
const TripEscalationsWithCity = (props) => {
  const { selectedCities } = useCity();
  return <TripEscalations {...props} selectedCities={selectedCities} />;
};

export default TripEscalationsWithCity;
